import { UseFormReturn } from 'react-hook-form';
import { SurveyFormData } from '@/utils/validation';

interface CustomerDetailsSectionProps {
  form: UseFormReturn<SurveyFormData>;
}

const CustomerDetailsSection = ({ form }: CustomerDetailsSectionProps) => {
  const {
    register,
    formState: { errors },
  } = form;

  return (
    <section aria-labelledby="section-a-heading" className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center mb-6">
        <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
          <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2
          id="section-a-heading"
          className="text-2xl font-bold text-gray-900"
        >
          Section A: Customer Details
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label
            htmlFor="customerName"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Customer Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <input
              type="text"
              id="customerName"
              {...register('details.customerName')}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors sm:text-sm text-gray-900 dark:text-gray-100"
              placeholder="Enter your full name"
              aria-invalid={errors.details?.customerName ? 'true' : 'false'}
              aria-describedby={
                errors.details?.customerName
                  ? 'customerName-error'
                  : undefined
              }
            />
          </div>
          {errors.details?.customerName && (
            <p
              id="customerName-error"
              className="mt-2 text-sm text-red-600 flex items-center"
              role="alert"
            >
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.details.customerName.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="companyName"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Company Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <input
              type="text"
              id="companyName"
              {...register('details.companyName')}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors sm:text-sm text-gray-900 dark:text-gray-100"
              placeholder="Your company name"
              aria-invalid={errors.details?.companyName ? 'true' : 'false'}
              aria-describedby={
                errors.details?.companyName ? 'companyName-error' : undefined
              }
            />
          </div>
          {errors.details?.companyName && (
            <p
              id="companyName-error"
              className="mt-2 text-sm text-red-600 flex items-center"
              role="alert"
            >
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.details.companyName.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="productOrService"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Product/Service Purchased <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <input
              type="text"
              id="productOrService"
              {...register('details.productOrService')}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors sm:text-sm text-gray-900 dark:text-gray-100"
              placeholder="Product or service name"
              aria-invalid={
                errors.details?.productOrService ? 'true' : 'false'
              }
              aria-describedby={
                errors.details?.productOrService
                  ? 'productOrService-error'
                  : undefined
              }
            />
          </div>
          {errors.details?.productOrService && (
            <p
              id="productOrService-error"
              className="mt-2 text-sm text-red-600 flex items-center"
              role="alert"
            >
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.details.productOrService.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="engagementDate"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Date of Engagement/Delivery <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <input
              type="date"
              id="engagementDate"
              {...register('details.engagementDate')}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors sm:text-sm text-gray-900 dark:text-gray-100"
              aria-invalid={errors.details?.engagementDate ? 'true' : 'false'}
              aria-describedby={
                errors.details?.engagementDate
                  ? 'engagementDate-error'
                  : undefined
              }
            />
          </div>
          {errors.details?.engagementDate && (
            <p
              id="engagementDate-error"
              className="mt-2 text-sm text-red-600 flex items-center"
              role="alert"
            >
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.details.engagementDate.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="contactPerson"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Contact Person <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <input
              type="text"
              id="contactPerson"
              {...register('details.contactPerson')}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors sm:text-sm text-gray-900 dark:text-gray-100"
              placeholder="Contact person name"
              aria-invalid={errors.details?.contactPerson ? 'true' : 'false'}
              aria-describedby={
                errors.details?.contactPerson
                  ? 'contactPerson-error'
                  : undefined
              }
            />
          </div>
          {errors.details?.contactPerson && (
            <p
              id="contactPerson-error"
              className="mt-2 text-sm text-red-600 flex items-center"
              role="alert"
            >
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.details.contactPerson.message}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default CustomerDetailsSection;

